import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { User } from '../entities/User';
import { UserProfile } from '../types/user';
import { CacheConfig, CacheStatus } from '../types/services';
import { userFactsService } from './userFactsService';
import { UserFactInput } from '../types/userFacts';

interface UserCache {
  profile: User | null;
  userId: string | null;  // Track which user the cache belongs to for security validation
  timestamp: number | null;
  isLoading: boolean;
  pendingPromise: Promise<User | null> | null;
}

export class UserService extends BaseService {
  private cache: UserCache = {
    profile: null,
    userId: null,
    timestamp: null,
    isLoading: false,
    pendingPromise: null
  };

  private readonly cacheConfig: CacheConfig = {
    ttl: 5 * 60 * 1000, // 5 minutes
    maxAge: 30 * 60 * 1000 // 30 minutes
  };

  constructor() {
    super('UserService', { debug: false });
  }

  async getProfile(accessTokenOrForceRefresh?: string | boolean, forceRefresh = false): Promise<User | null> {
    // Handle different parameter combinations for backward compatibility
    const actualForceRefresh = typeof accessTokenOrForceRefresh === 'boolean' ? 
      accessTokenOrForceRefresh : forceRefresh;

    if (!actualForceRefresh) {
      const cached = this.getCachedProfile();
      if (cached && this.isCacheValid()) {
        this.logDebug('Returning cached profile', {
          age: Date.now() - (this.cache.timestamp || 0)
        });
        return cached;
      }
    }

    // Check for in-flight request
    if (this.cache.isLoading && this.cache.pendingPromise) {
      this.logDebug('Profile fetch already in progress');
      return this.cache.pendingPromise;
    }

    // Create new request
    const promise = this.executeOperation(
      () => this._fetchProfile(),
      'getProfile'
    );

    this.cache.pendingPromise = promise;
    return promise;
  }

  private async _fetchProfile(): Promise<User | null> {
    try {
      this.cache.isLoading = true;
      
      const response = await apiService.get<UserProfile>('/api/user/profile');
      
      // The API returns the profile directly, not wrapped in { data: ... }
      const profileData = response;
      
      if (profileData) {
        const user = new User(profileData);
        this.setCacheProfile(user);
        this.applyDeveloperPreferences(user);
        return user;
      }
      
      return null;
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      if (error.status === 404) {
        this.logDebug('User profile not found (404)');
        return null;
      }
      throw error;
    } finally {
      this.cache.isLoading = false;
      this.cache.pendingPromise = null;
    }
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<User> {
    return this.executeOperation(async () => {
      const response = await apiService.put<UserProfile>(
        '/api/user/profile',
        updates
      );
      
      // The API returns the profile directly, not wrapped in { data: ... }
      const updatedProfileData = response;
      if (updatedProfileData) {
        const user = new User(updatedProfileData);
        this.setCacheProfile(user);
        
        // Sync preferences to UserFacts if relevant fields were updated
        await this.syncPreferencesToUserFacts(user, updates);
        
        return user;
      }
      
      throw new Error('Failed to update profile');
    }, 'updateProfile');
  }

  /**
   * Update a single field in the user profile for inline editing
   */
  async updateField<T extends keyof UserProfile>(
    field: T,
    value: UserProfile[T],
    options: { skipValidation?: boolean } = {}
  ): Promise<User> {
    return this.executeOperation(async () => {
      // Basic validation unless skipped
      if (!options.skipValidation) {
        this.validateFieldUpdate(field, value);
      }

      const updates: Partial<UserProfile> = {
        [field]: value
      };

      // Use the existing updateProfile method for consistency
      return await this.updateProfile(updates);
    }, 'updateField');
  }

  /**
   * Validate field updates for inline editing
   */
  private validateFieldUpdate<T extends keyof UserProfile>(
    field: T,
    value: UserProfile[T]
  ): void {
    switch (field) {
      case 'displayName':
        if (typeof value !== 'string' || value.trim().length === 0) {
          throw new Error('Display name cannot be empty');
        }
        if (value.trim().length > 100) {
          throw new Error('Display name cannot exceed 100 characters');
        }
        break;
      
      case 'email':
        if (typeof value !== 'string' || !value.includes('@')) {
          throw new Error('Invalid email address');
        }
        if (value.length > 255) {
          throw new Error('Email address cannot exceed 255 characters');
        }
        break;
      
      case 'language':
        if (typeof value !== 'string' || !['en', 'nl'].includes(value)) {
          throw new Error('Invalid language selection');
        }
        break;
      
      case 'timezone':
        if (typeof value !== 'string' || value.trim().length === 0) {
          throw new Error('Timezone cannot be empty');
        }
        break;
      
      default:
        // Allow other fields without specific validation
        break;
    }
  }

  /**
   * Synchronize user preferences to UserFacts for chat context
   */
  private async syncPreferencesToUserFacts(user: User, updates: Partial<UserProfile>): Promise<void> {
    try {
      const factsToUpdate: UserFactInput[] = [];
      
      // Check if display name was updated
      if (updates.displayName !== undefined) {
        factsToUpdate.push({
          key: 'preferences_name',
          value: updates.displayName,
          source: 'user_profile',
          confidence: 1.0,
          tags: ['preference', 'name', 'chat_context']
        });
      }
      
      // Check if language was updated
      if (updates.language !== undefined) {
        const languageDisplayName = updates.language === 'en' ? 'English' : 
                                   updates.language === 'nl' ? 'Dutch' : 
                                   updates.language;
        
        factsToUpdate.push({
          key: 'preferences_language',
          value: languageDisplayName,
          source: 'user_profile',
          confidence: 1.0,
          tags: ['preference', 'language', 'chat_context']
        });
      }
      
      // Check if timezone was updated
      if (updates.timezone !== undefined) {
        factsToUpdate.push({
          key: 'preferences_timezone',
          value: updates.timezone,
          source: 'user_profile',
          confidence: 1.0,
          tags: ['preference', 'timezone', 'chat_context']
        });
      }

      // Mood is now managed exclusively through UserFacts
      // No longer sync mood from user profile metadata to UserFacts
      
      // Update facts if there are any to sync
      if (factsToUpdate.length > 0) {
        this.logDebug('Syncing user preferences to UserFacts', {
          userId: user.id,
          factsCount: factsToUpdate.length,
          keys: factsToUpdate.map(f => f.key)
        });
        
        await userFactsService.createOrUpdateFacts(factsToUpdate, true);
      }
    } catch (error) {
      // Don't fail the profile update if UserFacts sync fails
      this.logDebug('Failed to sync preferences to UserFacts', {
        error: error instanceof Error ? error.message : 'Unknown error',
        userId: user.id
      });
    }
  }

  async createProfile(
    profileDataOrAccessToken: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'> | string, 
    profileData?: any
  ): Promise<User> {
    // Handle different parameter combinations for backward compatibility
    let actualProfileData: any;
    
    if (typeof profileDataOrAccessToken === 'string') {
      // Legacy signature: createProfile(accessToken, profileData)
      actualProfileData = profileData;
    } else {
      // New signature: createProfile(profileData)
      actualProfileData = profileDataOrAccessToken;
    }

    if (!actualProfileData) {
      throw new Error('Profile data is required');
    }

    return this.executeOperation(async () => {
      const response = await apiService.post<UserProfile>(
        '/api/user/profile',
        actualProfileData
      );
      
      // The API returns the profile directly, not wrapped in { data: ... }
      const newProfileData = response;
      if (newProfileData) {
        const user = new User(newProfileData);
        this.setCacheProfile(user);
        return user;
      }
      
      throw new Error('Failed to create profile');
    }, 'createProfile');
  }

  clearCache(): void {
    this.cache = {
      profile: null,
      userId: null,
      timestamp: null,
      isLoading: false,
      pendingPromise: null
    };
    this.logDebug('Cache cleared');
  }

  private isCacheValid(): boolean {
    if (!this.cache.profile || !this.cache.timestamp) {
      return false;
    }

    const age = Date.now() - this.cache.timestamp;
    return age < this.cacheConfig.ttl;
  }

  private setCacheProfile(profile: User): void {
    this.cache.profile = profile;
    this.cache.userId = profile.id;
    this.cache.timestamp = Date.now();
  }

  private getCachedProfile(): User | null {
    return this.cache.profile;
  }

  /**
   * Validate that cached profile belongs to the expected user.
   * Auto-invalidates cache if user ID mismatch detected (security protection).
   * @param expectedUserId - The user ID to validate against
   * @returns true if cache is valid for this user, false otherwise
   */
  validateCacheUser(expectedUserId: string): boolean {
    if (!this.cache.userId || !this.cache.profile) {
      return false;
    }
    
    if (this.cache.userId !== expectedUserId) {
      this.logDebug('Cache user mismatch detected - clearing cache for security', {
        cachedUserId: this.cache.userId,
        expectedUserId,
        action: 'auto-invalidate'
      });
      this.clearCache();
      return false;
    }
    
    return true;
  }

  private applyDeveloperPreferences(profile: User): void {
    if (profile?.metadata?.developerPreferences?.cacheDebugEnabled) {
      this.enableDebug();
    }
  }

  getCacheStatus(): CacheStatus {
    if (!this.cache.profile || !this.cache.timestamp) {
      return { status: 'empty', age: 0 };
    }

    const age = Date.now() - this.cache.timestamp;
    let status: CacheStatus['status'] = 'fresh';

    if (age > this.cacheConfig.maxAge) {
      status = 'expired';
    } else if (age > this.cacheConfig.ttl) {
      status = 'stale';
    }

    return {
      status,
      age,
      isLoading: this.cache.isLoading,
      ttl: this.cacheConfig.ttl,
      maxAge: this.cacheConfig.maxAge
    };
  }

  // Legacy methods for backward compatibility
  async getCurrentUser(): Promise<User | null> {
    return this.getProfile(false);
  }

  getCacheDebugInfo(): any {
    return {
      status: this.getCacheStatus(),
      config: this.cacheConfig,
      timestamp: this.cache.timestamp
    };
  }

  enableCacheDebug(): void {
    this.enableDebug();
  }

  disableCacheDebug(): void {
    this.disableDebug();
  }

  async deleteAccount(): Promise<void> {
    return this.executeOperation(async () => {
      await apiService.delete('/api/user/profile');
      this.clearCache();
    }, 'deleteAccount');
  }

  async completeOnboarding(formData: any): Promise<User> {
    return this.executeOperation(async () => {
      // No longer handle mood in onboarding - mood is managed exclusively through UserFacts
      const onboardingData = {
        ...formData,
        metadata: {
          ...formData.metadata,
          onboardingCompleted: true
        }
      };

      // Use PUT /api/user/profile for updating existing user profile (not POST for creation)
      const response = await apiService.put<UserProfile>(
        '/api/user/profile',
        onboardingData
      );
      
      // The API returns the profile directly, not wrapped in { data: ... }
      const updatedProfileData = response;
      if (updatedProfileData) {
        const user = new User(updatedProfileData);
        this.setCacheProfile(user);
        
        // Sync initial preferences to UserFacts (excluding mood)
        await this.syncPreferencesToUserFacts(user, {
          displayName: updatedProfileData.displayName,
          language: updatedProfileData.language,
          timezone: updatedProfileData.timezone,
          metadata: updatedProfileData.metadata
        });
        
        return user;
      }
      
      throw new Error('Failed to complete onboarding');
    }, 'completeOnboarding');
  }

  // Mood is now managed exclusively through UserFacts
  // Use updateMoodInUserFacts() instead

  // Mood expiration is now handled by UserFacts TTL and timestamps
  // Use getCurrentMoodFromUserFacts() to check if mood exists

  /**
   * Get current mood data from UserFacts using the wellbeing_mood_state key
   * Returns null if the fact doesn't exist or has expired
   */
  async getCurrentMoodFromUserFacts(): Promise<number | null> {
    return this.executeOperation(async () => {
      const currentUser = this.getCachedProfile();
      
      if (!currentUser) {
        throw new Error('No user profile found');
      }

      const factResponse = await userFactsService.getUserFact('wellbeing_mood_state');
      
      // getUserFact returns null if fact doesn't exist (404)
      if (!factResponse?.fact?.value) {
        this.logDebug('Mood fact not found or expired in UserFacts', { userId: currentUser.id });
        return null;
      }
      
      // Extract numeric value from string - handle formats like "6/10 - description" or just "6"
      let moodValue: number;
      const factValue = String(factResponse.fact.value).trim();
      
      // Try to extract number from the beginning of the string
      const match = factValue.match(/^(\d+(?:\.\d+)?)/);
      if (match) {
        moodValue = Number(match[1]);
      } else {
        // Fallback: try direct conversion
        moodValue = Number(factValue);
      }
      
      if (!isNaN(moodValue) && moodValue >= 1 && moodValue <= 10) {
        this.logDebug('Retrieved mood from UserFacts', {
          userId: currentUser.id,
          mood: moodValue,
          originalValue: factResponse.fact.value,
          lastUpdated: factResponse.fact.updatedAt
        });
        return moodValue;
      } else {
        this.logDebug('Invalid mood value from UserFacts', {
          userId: currentUser.id,
          value: factResponse.fact.value,
          extractedValue: moodValue
        });
        return null;
      }
    }, 'getCurrentMoodFromUserFacts');
  }

  /**
   * Get current mood data with timestamp from UserFacts using the wellbeing_mood_state key
   * Returns null values if the fact doesn't exist or has expired
   */
  async getCurrentMoodWithTimestamp(): Promise<{ mood: number | null; updatedAt: string | null }> {
    return this.executeOperation(async () => {
      // Use getCurrentUser() instead of getCachedProfile() for cache-safe operation
      const currentUser = await this.getCurrentUser();
      
      if (!currentUser) {
        // Graceful fallback: return null values instead of throwing
        this.logDebug('No user profile available for mood timestamp retrieval');
        return { mood: null, updatedAt: null };
      }

      const factResponse = await userFactsService.getUserFact('wellbeing_mood_state');
      
      // getUserFact returns null if fact doesn't exist (404)
      if (!factResponse?.fact?.value) {
        this.logDebug('Mood fact not found or expired in UserFacts', { userId: currentUser.id });
        return { mood: null, updatedAt: null };
      }
      
      // Extract numeric value from string - handle formats like "6/10 - description" or just "6"
      let moodValue: number;
      const factValue = String(factResponse.fact.value).trim();
      
      // Try to extract number from the beginning of the string
      const match = factValue.match(/^(\d+(?:\.\d+)?)/);
      if (match) {
        moodValue = Number(match[1]);
      } else {
        // Fallback: try direct conversion
        moodValue = Number(factValue);
      }
      
      if (!isNaN(moodValue) && moodValue >= 1 && moodValue <= 10) {
        this.logDebug('Retrieved mood with timestamp from UserFacts', {
          userId: currentUser.id,
          mood: moodValue,
          originalValue: factResponse.fact.value,
          lastUpdated: factResponse.fact.updatedAt
        });
        return {
          mood: moodValue,
          updatedAt: factResponse.fact.updatedAt || null
        };
      } else {
        this.logDebug('Invalid mood value from UserFacts', {
          userId: currentUser.id,
          value: factResponse.fact.value,
          extractedValue: moodValue
        });
        return { mood: null, updatedAt: null };
      }
    }, 'getCurrentMoodWithTimestamp');
  }

  /**
   * Check if the mood in UserFacts is older than 24 hours
   * @returns true if mood is older than 24 hours or doesn't exist, false if recent
   */
  async isMoodStale(): Promise<boolean> {
    return this.executeOperation(async () => {
      // Use getCurrentUser() for cache-safe operation
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        // Graceful fallback: consider missing profile as stale
        this.logDebug('No user profile available for mood staleness check');
        return true;
      }

      const factResponse = await userFactsService.getUserFact('wellbeing_mood_state');
      
      // getUserFact returns null if fact doesn't exist (404)
      if (!factResponse?.fact?.updatedAt) {
        this.logDebug('No mood fact found or no timestamp available', { userId: currentUser.id });
        return true; // Consider missing mood as stale
      }

      const updatedAt = new Date(factResponse.fact.updatedAt);
      const now = new Date();
      const hoursDiff = (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60);
      
      const isStale = hoursDiff > 24;
      
      this.logDebug('Mood staleness check', {
        userId: currentUser.id,
        updatedAt: factResponse.fact.updatedAt,
        hoursDiff: Math.round(hoursDiff * 100) / 100,
        isStale
      });
      
      return isStale;
    }, 'isMoodStale');
  }

  /**
   * Update mood directly in UserFacts (without affecting user profile)
   */
  async updateMoodInUserFacts(mood: number, userId?: string): Promise<void> {
    return this.executeOperation(async () => {
      let currentUserId = userId;
      
      // If no userId provided, try to get it from cached profile
      if (!currentUserId) {
        const currentUser = this.getCachedProfile();
        if (!currentUser) {
          throw new Error('No user profile found. Please provide userId parameter or ensure user profile is loaded.');
        }
        currentUserId = currentUser.id;
      }

      if (mood < 1 || mood > 10) {
        throw new Error('Mood value must be between 1 and 10');
      }

      const timestamp = new Date().toISOString();
      
      const factsToUpdate: UserFactInput[] = [
        {
          key: 'wellbeing_mood_state',
          value: mood,
          source: 'user_input',
          confidence: 1.0,
          tags: ['mood', 'wellbeing', 'current']
        }
      ];
      
      this.logDebug('Updating mood directly in UserFacts', {
        userId: currentUserId,
        mood,
        timestamp
      });
      
      await userFactsService.createOrUpdateFacts(factsToUpdate, true);
    }, 'updateMoodInUserFacts');
  }

  /**
   * Update a generic wellbeing fact in UserFacts
   * Used for stress, work-life balance, focus, energy, social support, sleep, etc.
   */
  async updateWellbeingFact(key: string, value: number, userId?: string): Promise<void> {
    return this.executeOperation(async () => {
      let currentUserId = userId;
      
      // If no userId provided, try to get it from cached profile
      if (!currentUserId) {
        const currentUser = this.getCachedProfile();
        if (!currentUser) {
          throw new Error('No user profile found. Please provide userId parameter or ensure user profile is loaded.');
        }
        currentUserId = currentUser.id;
      }

      if (value < 1 || value > 10) {
        throw new Error('Value must be between 1 and 10');
      }

      // Validate the key is a known wellbeing factor
      const validKeys = [
        'wellbeing_stress_level',
        'wellbeing_work_life_balance',
        'wellbeing_focus',
        'wellbeing_energy',
        'wellbeing_social_support',
        'wellbeing_sleep'
      ];
      
      if (!validKeys.includes(key)) {
        throw new Error(`Invalid wellbeing key: ${key}. Must be one of: ${validKeys.join(', ')}`);
      }

      const factsToUpdate: UserFactInput[] = [
        {
          key,
          value,
          source: 'user_input',
          confidence: 1.0,
          tags: ['wellbeing', 'current', key.replace('wellbeing_', '')]
        }
      ];
      
      this.logDebug('Updating wellbeing fact in UserFacts', {
        userId: currentUserId,
        key,
        value
      });
      
      await userFactsService.createOrUpdateFacts(factsToUpdate, true);
    }, 'updateWellbeingFact');
  }

  /**
   * Update a text-based wellbeing fact in UserFacts
   * Used for additional info, notes, etc.
   */
  async updateWellbeingTextFact(key: string, value: string, userId?: string): Promise<void> {
    return this.executeOperation(async () => {
      let currentUserId = userId;
      
      // If no userId provided, try to get it from cached profile
      if (!currentUserId) {
        const currentUser = this.getCachedProfile();
        if (!currentUser) {
          throw new Error('No user profile found. Please provide userId parameter or ensure user profile is loaded.');
        }
        currentUserId = currentUser.id;
      }

      if (!value || value.trim().length === 0) {
        throw new Error('Value cannot be empty');
      }

      const factsToUpdate: UserFactInput[] = [
        {
          key,
          value: value.trim(),
          source: 'user_input',
          confidence: 1.0,
          tags: ['wellbeing', 'current', 'text', key.replace('wellbeing_', '')]
        }
      ];
      
      this.logDebug('Updating wellbeing text fact in UserFacts', {
        userId: currentUserId,
        key,
        valueLength: value.trim().length
      });
      
      await userFactsService.createOrUpdateFacts(factsToUpdate, true);
    }, 'updateWellbeingTextFact');
  }

  // Mood timestamps are now managed by UserFacts automatically

  // Mood age is now tracked in UserFacts - use getUserFact to check timestamp

  // Mood syncing methods removed - mood is now managed exclusively through UserFacts

  async negotiate(_accessToken: string): Promise<{ url: string; accessToken: string }> {
    return this.executeOperation(async () => {
      try {
        // Due to binding, need to provide header to backend to work in local mode
        // For now, using basic post without custom headers - can be extended later if needed
        const negotiateData = await apiService.post<{ url: string; accessToken: string }>('/api/negotiate', {});

        return negotiateData;
      } catch (error) {
        console.error('[UserService] Error negotiating:', error);
        throw error;
      }
    }, 'negotiate');
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying ${operationName}`, { 
      attempt, 
      delay, 
      error: error.message 
    });
  }
}

export const userService = new UserService();
